import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { UserContext } from '../App';
import Logo from './Logo';

export default function Nominees({ embedded = false }) {
  const { currentUser, users, setCurrentUser } = useContext(UserContext);
  const [list, setList] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Form state
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    relationship: ''
  });

  // Relationship options
  const relationshipOptions = [
    'Spouse/Partner',
    'Child', 
    'Extended family',
    'Friend',
    'Other'
  ];

  // Asset owner is the current logged-in user
  const assetHolder = currentUser;
  if (!assetHolder) {
    return <div>Loading...</div>;
  }

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    api.get('/nominees').then(res => {
      setList(res.data.filter(b => b.ownerId === assetHolder.id));
    });
  }, [assetHolder.id]);

  const resetForm = () => {
    setForm({
      firstName: '',
      lastName: '',
      email: '',
      relationship: ''
    });
    setEditingBeneficiary(null);
    setShowAddForm(false);
    setError('');
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('First name, last name, and email are required');
      return false;
    }
    if (!form.relationship) {
      setError('Please select a relationship');
      return false;
    }
    if (!form.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const addBeneficiary = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    // Check if email already exists
    if (list.some(b => b.email.toLowerCase() === form.email.toLowerCase())) {
      setError('A nominee with this email already exists');
      return;
    }

    try {
      await api.post('/nominees', { 
        ownerId: assetHolder.id, 
        name: `${form.firstName} ${form.lastName}`.trim(), 
        email: form.email.trim(),
        role: form.relationship
      });
      const res = await api.get('/nominees');
      setList(res.data.filter(b => b.ownerId === assetHolder.id));
      setSuccess('Nominee added successfully!');
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed adding nominee');
    }
  };

  const startEdit = (beneficiary) => {
    const [firstName, ...lastNameParts] = beneficiary.name.split(' ');
    const lastName = lastNameParts.join(' ');
    setForm({
      firstName: firstName || '',
      lastName: lastName || '',
      email: beneficiary.email,
      relationship: beneficiary.role || ''
    });
    setEditingBeneficiary(beneficiary);
    setShowAddForm(true);
    setError('');
  };

  const updateBeneficiary = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    // Check if email already exists (excluding current nominee)
    if (list.some(b => b.id !== editingBeneficiary.id && b.email.toLowerCase() === form.email.toLowerCase())) {
      setError('A nominee with this email already exists');
      return;
    }

    try {
      await api.put(`/nominees/${editingBeneficiary.id}`, {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email.trim(),
        role: form.relationship
      });
      const res = await api.get('/nominees');
      setList(res.data.filter(b => b.ownerId === assetHolder.id));
      setSuccess('Nominee updated successfully!');
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed updating nominee');
    }
  };

  const removeBeneficiary = async (id) => {
    if (list.length <= 1) {
      setError('At least one nominee is required. Please add a new nominee before removing this one.');
      return;
    }

    if (window.confirm('Are you sure you want to remove this nominee?')) {
      try {
        await api.delete(`/nominees/${id}`);
        const res = await api.get('/nominees');
        setList(res.data.filter(b => b.ownerId === assetHolder.id));
        setSuccess('Nominee removed successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error(err);
        setError('Failed removing nominee');
      }
    }
  };

  const cancelEdit = () => {
    resetForm();
  };

  // Sidebar styles
  const sidebarStyle = {
    width: 220,
    background: 'var(--dusk-700)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    padding: '32px 0 18px 0',
    boxShadow: '2px 0 12px rgba(30,40,60,0.04)',
    position: 'fixed',
    left: 0,
    top: 0,
    height: '100vh',
    zIndex: 100,
  };
  const mobileSidebarStyle = {
    ...sidebarStyle,
    width: 180,
    minWidth: 0,
    maxWidth: '80vw',
    transition: 'transform 0.3s',
    transform: 'translateX(-100%)',
  };
  const mobileSidebarOpenStyle = {
    ...mobileSidebarStyle,
    transform: 'translateX(0)',
  };

  // If viewing as nominee, show only their info
  if (!embedded && currentUser.type === 'nominee') {
    // Get all owners who have this nominee in their list
    const [donors, setDonors] = useState([]);
    
    useEffect(() => {
      Promise.all([
        api.get('/nominees'),
        api.get('/auth/users')
      ]).then(([benRes, usersRes]) => {
        const allBeneficiaries = benRes.data;
        const allUsers = usersRes.data;
        const donorIds = [...new Set(allBeneficiaries
          .filter(b => b.email === currentUser.email)
          .map(b => b.ownerId))];
        
        // Ensure we don't have duplicate donor IDs
        const uniqueDonorIds = [...new Set(donorIds)];
        // Get owner information
        const donorInfo = uniqueDonorIds.map(ownerId => {
          const donor = allUsers.find(u => u.id === ownerId);
          console.log('Nominees - Mapping owner for ownerId:', ownerId, 'found owner:', donor);
          return {
            id: ownerId,
            name: donor ? donor.name : 'Unknown Owner',
            email: donor ? donor.email : 'Unknown Email'
          };
        });
        console.log('Nominees - Final ownerInfo:', donorInfo);
        setDonors(donorInfo);
      });
    }, [currentUser.email]);

    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f7f8fa' }}>
        {/* Mobile hamburger */}
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{ position: 'fixed', top: 18, left: 18, zIndex: 2100, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 12px', fontSize: 22, boxShadow: 'var(--shadow-soft)', cursor: 'pointer' }}
            aria-label="Open menu"
          >
            ☰
          </button>
        )}
        {/* Sidebar */}
        <nav
          style={
            isMobile
              ? (sidebarOpen ? mobileSidebarOpenStyle : mobileSidebarStyle)
              : sidebarStyle
          }
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 26, letterSpacing: 1, textAlign: 'center', marginBottom: 38, color: '#fff' }}>ProVault</div>
            {/* User indicator */}
            <div style={{ background: 'var(--sage-600)', color: '#fff', borderRadius: 12, padding: '10px 16px', margin: '0 18px 18px 18px', fontSize: 15, fontWeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, boxShadow: 'var(--shadow-soft)' }}>
              <span>Logged in as:</span>
              <span style={{ fontWeight: 700 }}>{currentUser.name}</span>
              <span style={{ fontSize: 13, opacity: 0.85 }}>{currentUser.email}</span>
              {currentUser.role && <span style={{ fontSize: 13, opacity: 0.7 }}>({currentUser.role})</span>}
            </div>
            
            {/* Dashboard */}
            <div style={{ marginBottom: 24 }}>
              <button
                onClick={() => { navigate('/dashboard'); setSidebarOpen(false); }}
                style={{
                  background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                }}
              >
                Dashboard
              </button>
            </div>

            {/* Assets Section */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#888', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Assets</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <button
                  onClick={() => { navigate('/financial-assets'); setSidebarOpen(false); }}
                  style={{
                    background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                  }}
                >
                  Financial Assets
                </button>
                <button
                  onClick={() => { navigate('/legal-assets'); setSidebarOpen(false); }}
                  style={{
                    background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                  }}
                >
                  Legal Assets
                </button>
                <button
                  onClick={() => { navigate('/other-assets'); setSidebarOpen(false); }}
                  style={{
                    background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                  }}
                >
                  Other Assets
                </button>
              </div>
            </div>

            {/* Account button in menu */}
            <div style={{ marginBottom: 24 }}>
              <button
                onClick={() => { navigate('/account'); setSidebarOpen(false); }}
                style={{ background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none' }}
              >
                Account
              </button>
            </div>
          </div>
        </nav>

        {/* Main content */}
        <div style={{ flex: 1, marginLeft: isMobile ? 0 : 220, padding: isMobile ? '60px 0 0 0' : '0 0 0 0', minHeight: '100vh', overflow: 'auto' }}>
          <div style={{ padding: 20, position: 'relative' }}>
            <div style={{ maxWidth: 800, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(30,40,60,0.10)', padding: 32 }}>
              <h1 style={{ marginBottom: 24, fontWeight: 700, fontSize: 24, color: '#222' }}>Your Owners</h1>
              {donors.length === 0 ? (
                <div style={{ 
                  padding: '32px', 
                  textAlign: 'center', 
                  background: '#f8f9fa', 
                  borderRadius: 8, 
                  border: '2px dashed #dee2e6',
                  color: '#6c757d'
                }}>
                  <p style={{ fontSize: 16, margin: 0 }}>No owners have declared you as a nominee yet.</p>
                  <p style={{ fontSize: 14, margin: '8px 0 0 0' }}>When someone adds you as a nominee, they will appear here.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {donors.map(donor => (
                    <div key={donor.id} style={{ 
                      padding: '20px', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 8, 
                      background: '#fafafa'
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 18, color: '#333', marginBottom: 8 }}>{donor.name}</div>
                      <div style={{ fontSize: 14, color: '#666' }}>{donor.email}</div>
                      <div style={{ marginTop: 12 }}>
                        <button
                          onClick={() => {
                            // Set the selected owner ID in localStorage so Dashboard can pick it up
                            console.log('Nominees - Setting selectedOwnerId in localStorage:', donor.id, 'for owner:', donor.name);
                            console.log('Nominees - Owner object:', donor);
                            localStorage.setItem('selectedOwnerId', donor.id);
                            console.log('Nominees - Navigate to dashboard');
                            navigate('/dashboard');
                          }}
                          style={{
                            padding: '8px 16px',
                            fontSize: 14,
                            background: 'var(--sage-600)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontWeight: 500,
                            cursor: 'pointer'
                          }}
                        >
                          View Assets
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Asset holder view
  if (!embedded) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--mist-100)' }}>
      {/* Mobile hamburger */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{ position: 'fixed', top: 18, left: 18, zIndex: 2100, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 12px', fontSize: 22, boxShadow: 'var(--shadow-soft)', cursor: 'pointer' }}
          aria-label="Open menu"
        >
          ☰
        </button>
      )}
      {/* Sidebar */}
      <nav
        style={
          isMobile
            ? (sidebarOpen ? mobileSidebarOpenStyle : mobileSidebarStyle)
            : sidebarStyle
        }
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <Logo size={36} />
          </div>
          {/* User indicator */}
            <div style={{ background: 'var(--sage-600)', color: '#fff', borderRadius: 12, padding: '10px 16px', margin: '0 18px 18px 18px', fontSize: 15, fontWeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, boxShadow: 'var(--shadow-soft)' }}>
            <span>Logged in as:</span>
            <span style={{ fontWeight: 700 }}>{currentUser.name}</span>
            <span style={{ fontSize: 13, opacity: 0.85 }}>{currentUser.email}</span>
            {currentUser.role && <span style={{ fontSize: 13, opacity: 0.7 }}>({currentUser.role})</span>}
          </div>
          
          {/* Dashboard */}
          <div style={{ marginBottom: 24 }}>
            <button
              onClick={() => { navigate('/dashboard'); setSidebarOpen(false); }}
              style={{
                background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
              }}
            >
              Dashboard
            </button>
          </div>

          {/* Assets Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Assets</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button
                onClick={() => { navigate('/financial-assets'); setSidebarOpen(false); }}
                style={{
                  background: 'rgba(255,255,255,0.08)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                }}
              >
                Financial Assets
              </button>
              <button
                onClick={() => { navigate('/legal-assets'); setSidebarOpen(false); }}
                style={{
                  background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                }}
              >
                Legal Assets
              </button>
              <button
                onClick={() => { navigate('/other-assets'); setSidebarOpen(false); }}
                style={{
                  background: '#29304a', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none',
                }}
              >
                Other Assets
              </button>
            </div>
          </div>

          {/* Account dropdown */}
          <div style={{ position: 'relative', margin: '32px 18px 0 18px' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 0', borderRadius: 6, fontWeight: 500, fontSize: 15, color: '#fff', background: accountMenuOpen ? '#29304a' : 'none' }}
              onMouseEnter={() => setAccountMenuOpen(true)}
              onMouseLeave={() => setAccountMenuOpen(false)}
              onClick={() => isMobile && setAccountMenuOpen(!accountMenuOpen)}
            >
              <span style={{ fontSize: 18, marginRight: 4 }}>👤</span>
              Account
              <span style={{ marginLeft: 'auto', fontSize: 14 }}>▼</span>
            </div>
            {accountMenuOpen && (
              <div
                style={{ background: '#fff', color: '#222b45', borderRadius: 8, boxShadow: '0 4px 16px rgba(30,40,60,0.13)', minWidth: 140, zIndex: 10, padding: '8px 0' }}
                onMouseEnter={() => setAccountMenuOpen(true)}
                onMouseLeave={() => setAccountMenuOpen(false)}
              >
                <button
                  onClick={() => { navigate('/profile'); setAccountMenuOpen(false); setSidebarOpen(false); }}
                  style={{ width: '100%', background: 'none', border: 'none', color: '#222b45', fontWeight: 500, fontSize: 15, padding: '10px 18px', textAlign: 'left', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  Your profile
                </button>
                <button
                  onClick={() => { navigate(currentUser.type === 'nominee' ? '/owners' : '/nominees'); setAccountMenuOpen(false); setSidebarOpen(false); }}
                  style={{ width: '100%', background: 'none', border: 'none', color: '#222b45', fontWeight: 500, fontSize: 15, padding: '10px 18px', textAlign: 'left', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  {currentUser.type === 'nominee' ? 'Owners' : 'Nominees'}
                </button>
                <button
                  onClick={() => { setCurrentUser(null); navigate('/login'); setSidebarOpen(false); }}
                  style={{ width: '100%', background: 'none', border: 'none', color: '#222b45', fontWeight: 500, fontSize: 15, padding: '10px 18px', textAlign: 'left', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : 220, padding: isMobile ? '60px 0 0 0' : '0 0 0 0', minHeight: '100vh', overflow: 'auto' }}>
        <div style={{ padding: 20, position: 'relative' }}>
          <div style={{ maxWidth: 800, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(30,40,60,0.10)', padding: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h1 style={{ fontWeight: 700, fontSize: 24, color: '#222', margin: 0 }}>Nominees</h1>
              {!showAddForm && (
                <button
                  onClick={() => setShowAddForm(true)}
                  style={{
                    padding: '10px 20px',
                    fontSize: 15,
                    background: 'var(--sage-600)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: 'var(--shadow-soft)'
                  }}
                >
                  Add Nominee
                </button>
              )}
            </div>

            {/* Success/Error messages */}
            {success && (
              <div style={{ 
                padding: '12px 16px', 
                background: '#e8f5e8', 
                color: '#2e7d32', 
                borderRadius: 6, 
                marginBottom: 20, 
                border: '1px solid #c8e6c9',
                fontWeight: 500
              }}>
                {success}
              </div>
            )}
            {error && (
              <div style={{ 
                padding: '12px 16px', 
                background: '#ffebee', 
                color: '#c62828', 
                borderRadius: 6, 
                marginBottom: 20, 
                border: '1px solid #ffcdd2',
                fontWeight: 500
              }}>
                {error}
              </div>
            )}

            {/* Add/Edit Form */}
            {showAddForm && (
              <div style={{ 
                background: '#f8f9fa', 
                padding: 24, 
                borderRadius: 8, 
                marginBottom: 24,
                border: '1px solid #e9ecef'
              }}>
                <h3 style={{ marginBottom: 16, fontWeight: 600, fontSize: 18, color: '#333' }}>
                  {editingBeneficiary ? 'Edit Nominee' : 'Add New Nominee'}
                </h3>
                <form onSubmit={editingBeneficiary ? updateBeneficiary : addBeneficiary} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>First Name *</label>
                      <input 
                        value={form.firstName} 
                        onChange={e => handleFormChange('firstName', e.target.value)} 
                        placeholder="First Name" 
                        required 
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>Last Name *</label>
                      <input 
                        value={form.lastName} 
                        onChange={e => handleFormChange('lastName', e.target.value)} 
                        placeholder="Last Name" 
                        required 
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>Email Address *</label>
                    <input 
                      type="email" 
                      value={form.email} 
                      onChange={e => handleFormChange('email', e.target.value)} 
                      placeholder="Email Address" 
                      required 
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>Relationship *</label>
                    <select 
                      value={form.relationship} 
                      onChange={e => handleFormChange('relationship', e.target.value)}
                      required
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                    >
                      <option value="">Select Relationship</option>
                      {relationshipOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button 
                      type="submit" 
                      style={{ 
                        padding: '12px 24px', 
                        fontSize: 16, 
                        background: 'var(--sage-600)', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 12, 
                        fontWeight: 600, 
                        cursor: 'pointer',
                        boxShadow: 'var(--shadow-soft)'
                      }}
                    >
                      {editingBeneficiary ? 'Update Nominee' : 'Add Nominee'}
                    </button>
                    <button 
                      type="button"
                      onClick={cancelEdit}
                      style={{ 
                        padding: '12px 24px', 
                        fontSize: 16, 
                        background: '#6c757d', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 6, 
                        fontWeight: 600, 
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Current nominees list */}
            <div>
              <h3 style={{ fontWeight: 600, fontSize: 20, marginBottom: 16 }}>Current Nominees</h3>
              {list.length === 0 ? (
                <div style={{ 
                  padding: '32px', 
                  textAlign: 'center', 
                  background: '#f8f9fa', 
                  borderRadius: 8, 
                  border: '2px dashed #dee2e6',
                  color: '#6c757d'
                }}>
                  <p style={{ fontSize: 16, margin: 0 }}>No nominees added yet.</p>
                  <p style={{ fontSize: 14, margin: '8px 0 0 0' }}>Click "Add Nominee" to get started.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {list.map(b => (
                    <div key={b.id} style={{ 
                      padding: '20px', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 8, 
                      background: '#fff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 16, color: '#333' }}>{b.name}</div>
                        <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>{b.email}</div>
                        {b.role && (
                          <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>
                            <span style={{ fontWeight: 500 }}>Relationship:</span> {b.role}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => startEdit(b)}
                          style={{
                            padding: '8px 16px',
                            fontSize: 14,
                            background: 'var(--sage-600)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 12,
                            fontWeight: 500,
                            cursor: 'pointer',
                            boxShadow: 'var(--shadow-soft)'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => removeBeneficiary(b.id)}
                          disabled={list.length <= 1}
                          style={{
                            padding: '8px 16px',
                            fontSize: 14,
                            background: list.length <= 1 ? '#6c757d' : '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontWeight: 500,
                            cursor: list.length <= 1 ? 'not-allowed' : 'pointer',
                            opacity: list.length <= 1 ? 0.6 : 1
                          }}
                          title={list.length <= 1 ? 'At least one nominee is required' : 'Remove nominee'}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  // Embedded: render only the main content (owner view)
  return (
    <div style={{ padding: 20 }}>
      <div style={{ maxWidth: 800, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(30,40,60,0.10)', padding: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={{ fontWeight: 700, fontSize: 24, color: '#222', margin: 0 }}>Nominees</h1>
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                padding: '10px 20px',
                fontSize: 15,
                background: 'var(--sage-600)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: 'var(--shadow-soft)'
              }}
            >
              Add Nominee
            </button>
          )}
        </div>
        {success && (
          <div style={{ padding: '12px 16px', background: '#e8f5e8', color: '#2e7d32', borderRadius: 6, marginBottom: 20, border: '1px solid #c8e6c9', fontWeight: 500 }}>
            {success}
          </div>
        )}
        {error && (
          <div style={{ padding: '12px 16px', background: '#ffebee', color: '#c62828', borderRadius: 6, marginBottom: 20, border: '1px solid #ffcdd2', fontWeight: 500 }}>
            {error}
          </div>
        )}
        {showAddForm && (
          <div style={{ background: '#f8f9fa', padding: 24, borderRadius: 8, marginBottom: 24, border: '1px solid #e9ecef' }}>
            <h3 style={{ marginBottom: 16, fontWeight: 600, fontSize: 18, color: '#333' }}>
              {editingBeneficiary ? 'Edit Nominee' : 'Add New Nominee'}
            </h3>
            <form onSubmit={editingBeneficiary ? updateBeneficiary : addBeneficiary} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>First Name *</label>
                  <input 
                    value={form.firstName} 
                    onChange={e => handleFormChange('firstName', e.target.value)} 
                    placeholder="First Name" 
                    required 
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>Last Name *</label>
                  <input 
                    value={form.lastName} 
                    onChange={e => handleFormChange('lastName', e.target.value)} 
                    placeholder="Last Name" 
                    required 
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>Email Address *</label>
                <input 
                  type="email" 
                  value={form.email} 
                  onChange={e => handleFormChange('email', e.target.value)} 
                  placeholder="Email Address" 
                  required 
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>Relationship *</label>
                <select 
                  value={form.relationship} 
                  onChange={e => handleFormChange('relationship', e.target.value)}
                  required
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                >
                  <option value="">Select Relationship</option>
                  {relationshipOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button 
                  type="submit" 
                  style={{ padding: '12px 24px', fontSize: 16, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 600, cursor: 'pointer', boxShadow: 'var(--shadow-soft)' }}
                >
                  {editingBeneficiary ? 'Update Nominee' : 'Add Nominee'}
                </button>
                <button 
                  type="button"
                  onClick={cancelEdit}
                  style={{ padding: '12px 24px', fontSize: 16, background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
        <div>
          <h3 style={{ fontWeight: 600, fontSize: 20, marginBottom: 16 }}>Current Nominees</h3>
          {list.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', background: '#f8f9fa', borderRadius: 8, border: '2px dashed #dee2e6', color: '#6c757d' }}>
              <p style={{ fontSize: 16, margin: 0 }}>No nominees added yet.</p>
              <p style={{ fontSize: 14, margin: '8px 0 0 0' }}>Click "Add Nominee" to get started.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {list.map(b => (
                <div key={b.id} style={{ padding: '20px', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 16, color: '#333' }}>{b.name}</div>
                    <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>{b.email}</div>
                    {b.role && (
                      <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>
                        <span style={{ fontWeight: 500 }}>Relationship:</span> {b.role}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => startEdit(b)} style={{ padding: '8px 16px', fontSize: 14, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 500, cursor: 'pointer', boxShadow: 'var(--shadow-soft)' }}>Edit</button>
                    <button onClick={() => removeBeneficiary(b.id)} disabled={list.length <= 1} style={{ padding: '8px 16px', fontSize: 14, background: list.length <= 1 ? '#6c757d' : '#dc3545', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 500, cursor: list.length <= 1 ? 'not-allowed' : 'pointer', opacity: list.length <= 1 ? 0.6 : 1 }} title={list.length <= 1 ? 'At least one nominee is required' : 'Remove nominee'}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}